import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Bookmark, BookmarkCheck, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import api from "../lib/api";

export default function Bookmarks() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [revealed, setRevealed] = useState({});
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/bookmarks").then(({ data }) => setQuestions(data.questions || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    try {
      await api.post("/bookmarks/toggle", { question_id: id });
      setQuestions((qs) => qs.filter((q) => q.id !== id));
      toast.success("Removed from bookmarks");
    } catch { toast.error("Could not remove bookmark."); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Button variant="ghost" onClick={() => navigate("/study")} className="mb-4" data-testid="bookmarks-back-button"><ArrowLeft className="w-4 h-4 mr-1" /> Study Bank</Button>
        <div className="flex items-center gap-3 mb-6">
          <Bookmark className="w-7 h-7 text-amber-500" />
          <h1 className="font-heading text-4xl font-bold text-slate-900">Saved Questions</h1>
        </div>

        {loading ? null : questions.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-12 text-center">
            <Bookmark className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h3 className="font-heading text-2xl text-slate-900">No bookmarks yet</h3>
            <p className="text-slate-500 mt-1">Tap the bookmark icon on any question to save it here for later review.</p>
            <Button onClick={() => navigate("/study")} className="mt-5 bg-primary hover:bg-primary/90" data-testid="bookmarks-empty-cta">Go to Study Bank</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {questions.map((q, i) => (
              <motion.div key={q.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                className="bg-white border border-slate-200 rounded-xl p-6" data-testid={`bookmark-card-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <Badge className="bg-primary/10 text-primary hover:bg-primary/10">{q.category}</Badge>
                  <button onClick={() => remove(q.id)} className="text-amber-500 hover:text-red-500" data-testid={`bookmark-remove-${i}`}><BookmarkCheck className="w-5 h-5" /></button>
                </div>
                <p className="font-heading text-xl font-semibold text-slate-900 mt-3">{q.question}</p>
                <button onClick={() => setRevealed({ ...revealed, [q.id]: !revealed[q.id] })}
                  className="mt-3 text-sm text-primary font-medium flex items-center gap-1" data-testid={`bookmark-reveal-${i}`}>
                  {revealed[q.id] ? <><EyeOff className="w-4 h-4" /> Hide answer</> : <><Eye className="w-4 h-4" /> Show answer</>}
                </button>
                {revealed[q.id] && (
                  <div className="mt-3 p-4 rounded-lg bg-slate-50 border border-slate-200">
                    <p className="text-sm text-green-600 font-semibold uppercase tracking-widest">Answer: {q.correct}</p>
                    <p className="font-medium text-slate-900 mt-1">{q.options[q.correct]}</p>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed">{q.explanation}</p>
                    {q.concept_note && <p className="text-xs mt-2 text-slate-500" data-testid="concept-note">{q.concept_note}</p>}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
